import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Upload, Loader2, Copy, Ban, RefreshCw, Download, Star, ChevronDown } from "lucide-react";
import {
    fetchFeedbackEvents, createFeedbackEvent, patchFeedbackEvent, uploadContacts, fetchContacts,
    revokeContact, regenerateLink, fetchFeedbackResponses, overrideSentiment,
    fetchCoupons, revokeCoupon, feedbackExport, formatApiError,
} from "@/lib/api";
import { MessageGenerator } from "@/components/admin/MessageGenerator";
import { CampaignTracker } from "@/components/admin/CampaignTracker";

const inp = "w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-saffron";
const lab = "block text-[11px] font-bold uppercase tracking-wider text-stone-500 mb-1";
const SENT_CHIP = { positive: "bg-green-100 text-green-700", negative: "bg-red-100 text-red-600", neutral: "bg-gold/20 text-yellow-800" };
const COUPON_CHIP = { active: "bg-green-100 text-green-700", redeemed: "bg-blue-100 text-blue-700", revoked: "bg-red-100 text-red-600", locked: "bg-stone-200 text-stone-600" };

export function FeedbackManager({ token }) {
    const [events, setEvents] = useState(null);
    const [eventId, setEventId] = useState("");
    const [view, setView] = useState("contacts"); // contacts | responses | coupons | messages | campaigns
    const [focusCampaign, setFocusCampaign] = useState(null);
    const [newEvent, setNewEvent] = useState({ name: "", event_date: "" });
    const [creating, setCreating] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await fetchFeedbackEvents(token);
            setEvents(res.events);
            if (!eventId && res.events.length) setEventId(res.events[0].id);
        } catch { toast.error("Could not load feedback events"); }
        // eslint-disable-next-line
    }, [token]);

    useEffect(() => { load(); }, [load]);

    const createEv = async () => {
        if (!newEvent.name.trim()) { toast.error("Event name required"); return; }
        setCreating(true);
        try {
            const ev = await createFeedbackEvent(token, { name: newEvent.name, event_date: newEvent.event_date || null });
            toast.success("Event created");
            setNewEvent({ name: "", event_date: "" });
            await load();
            setEventId(ev.id);
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
        finally { setCreating(false); }
    };

    const current = (events || []).find((e) => e.id === eventId);

    return (
        <section className="bg-white rounded-2xl border border-stone-300 p-6" data-testid="feedback-manager">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h2 className="font-display font-black text-lg text-navy">Picnic Feedback & Coupons</h2>
                    <p className="text-xs text-stone-500 mt-1">Upload parent sheets → share secure links → collect ratings → auto-issue 10% coupons.</p>
                </div>
            </div>

            {/* event picker + create */}
            <div className="mt-4 flex flex-wrap items-end gap-3">
                <div className="min-w-[240px] flex-1">
                    <label className={lab}>Event</label>
                    <select className={inp} value={eventId} onChange={(e) => setEventId(e.target.value)} data-testid="fb-event-select">
                        {(events || []).map((e) => <option key={e.id} value={e.id}>{e.name}{e.event_date ? ` — ${e.event_date}` : ""}</option>)}
                        {(events || []).length === 0 && <option value="">No events yet</option>}
                    </select>
                </div>
                <div className="flex items-end gap-2 flex-wrap">
                    <div><label className={lab}>New event name</label><input className={inp} value={newEvent.name} onChange={(e) => setNewEvent((s) => ({ ...s, name: e.target.value }))} placeholder="Std 5 Picnic — Kanha" data-testid="fb-new-event-name" /></div>
                    <div><label className={lab}>Date</label><input type="date" className={inp} value={newEvent.event_date} onChange={(e) => setNewEvent((s) => ({ ...s, event_date: e.target.value }))} data-testid="fb-new-event-date" /></div>
                    <button onClick={createEv} disabled={creating} data-testid="fb-create-event" className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-saffron text-white text-xs font-display font-black uppercase tracking-wider hover:bg-saffron-dark disabled:opacity-60">
                        {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Create
                    </button>
                </div>
            </div>

            {/* stats */}
            {current?.stats && (
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="fb-stats">
                    {[["Contacts", current.stats.contacts], ["Submissions", current.stats.submissions], ["Response", `${current.stats.response_pct}%`],
                      ["Avg overall", current.stats.avg_overall != null ? `${current.stats.avg_overall}★` : "—"],
                      ["Coupons", `${current.stats.redeemed}/${current.stats.coupons} redeemed`]].map(([l, v]) => (
                        <div key={l} className="rounded-xl bg-cream/70 border border-stone-200 p-3 text-center">
                            <p className="font-display font-black text-lg text-navy">{v}</p>
                            <p className="text-[10px] font-bold uppercase tracking-wider text-stone-500">{l}</p>
                        </div>
                    ))}
                </div>
            )}

            {current && (
                <label className="mt-3 inline-flex items-center gap-2 text-sm font-bold text-navy cursor-pointer">
                    <input type="checkbox" className="accent-saffron" checked={current.rewards_enabled} data-testid="fb-rewards-toggle"
                        onChange={async (e) => { await patchFeedbackEvent(token, current.id, { rewards_enabled: e.target.checked }); load(); toast.success(e.target.checked ? "Rewards on — coupons will be issued" : "Rewards off — no coupons for this event"); }} />
                    Rewards enabled (10% coupon per feedback)
                </label>
            )}

            {/* sub-tabs */}
            <div className="mt-5 flex gap-2 border-b border-stone-200 pb-3">
                {[["contacts", "Contacts & Links"], ["responses", "Responses"], ["coupons", "Coupons"], ["messages", "Messages"], ["campaigns", "Campaigns"]].map(([k, l]) => (
                    <button key={k} onClick={() => setView(k)} data-testid={`fb-tab-${k}`}
                        className={`rounded-full px-4 py-2 text-[11px] font-display font-black uppercase tracking-wider transition-colors ${view === k ? "bg-navy text-white" : "bg-stone-100 text-stone-500 hover:bg-stone-200"}`}>{l}</button>
                ))}
                <button onClick={() => feedbackExport(token, view === "coupons" ? "coupons" : view === "contacts" ? "contacts" : "responses", eventId)}
                    data-testid="fb-export" className={`ml-auto inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider text-navy hover:text-saffron ${["messages", "campaigns"].includes(view) ? "hidden" : ""}`}>
                    <Download className="h-3.5 w-3.5" /> Export CSV
                </button>
            </div>

            {eventId && view === "contacts" && <ContactsPanel token={token} eventId={eventId} onChanged={load} />}
            {eventId && view === "responses" && <ResponsesPanel token={token} eventId={eventId} />}
            {eventId && view === "coupons" && <CouponsPanel token={token} eventId={eventId} />}
            {eventId && view === "messages" && (
                <MessageGenerator token={token} eventId={eventId} events={events || []}
                    onCampaignCreated={(cid) => { setFocusCampaign(cid); setView("campaigns"); load(); }} />
            )}
            {view === "campaigns" && <CampaignTracker token={token} events={events || []} focusCampaign={focusCampaign} />}
        </section>
    );
}

/* ---------------- Contacts: upload + link management ---------------- */
function ContactsPanel({ token, eventId, onChanged }) {
    const [contacts, setContacts] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [lastLinks, setLastLinks] = useState(null);
    const fileRef = useRef(null);

    const load = useCallback(async () => {
        try { setContacts((await fetchContacts(token, eventId)).contacts); } catch { toast.error("Load failed"); }
    }, [token, eventId]);
    useEffect(() => { setContacts(null); load(); }, [load]);

    const doUpload = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const res = await uploadContacts(token, eventId, file);
            setLastLinks(res.links);
            toast.success(`${res.added} contacts added · ${res.duplicates} duplicates skipped · ${res.invalid.length} invalid rows`);
            load();
            onChanged();
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
        finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
    };

    const downloadLinks = () => {
        const csv = ["name,phone,feedback_link", ...lastLinks.map((l) => `${l.name},${l.phone},"${l.link}"`)].join("\n");
        const a = document.createElement("a");
        a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
        a.download = "feedback-links.csv";
        a.click();
    };

    const regen = async (c) => {
        try {
            const res = await regenerateLink(token, c.id);
            await navigator.clipboard.writeText(res.link).catch(() => {});
            toast.success("New link generated & copied — old link is now dead");
            load();
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    };

    return (
        <div className="mt-4">
            <div className="rounded-xl border-2 border-dashed border-stone-300 p-5 text-center" data-testid="fb-upload-zone">
                <input ref={fileRef} type="file" accept=".csv,.xlsx" className="hidden" data-testid="fb-upload-input" onChange={(e) => doUpload(e.target.files[0])} />
                <Upload className="h-6 w-6 text-stone-400 mx-auto" />
                <p className="mt-1.5 text-sm font-bold text-navy">Upload parent sheet (CSV / Excel)</p>
                <p className="text-xs text-stone-500">Columns: name, mobile, visit date — one row per family</p>
                <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="fb-upload-btn"
                    className="mt-3 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-navy text-white text-xs font-display font-black uppercase tracking-wider hover:bg-navy-light disabled:opacity-60">
                    {uploading && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Choose File
                </button>
                {lastLinks && (
                    <button onClick={downloadLinks} data-testid="fb-download-links" className="mt-2 ml-2 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full border border-saffron text-saffron text-xs font-black uppercase tracking-wider hover:bg-saffron hover:text-white transition-colors">
                        <Download className="h-3.5 w-3.5" /> Download {lastLinks.length} links (CSV)
                    </button>
                )}
            </div>

            {contacts === null ? <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-saffron" /></div> : (
                <div className="mt-4 overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead><tr className="text-left text-[11px] uppercase tracking-wider text-stone-500 border-b border-stone-200">
                            <th className="py-2 pr-4">Name</th><th className="py-2 pr-4">Mobile</th><th className="py-2 pr-4">Visit date</th><th className="py-2 pr-4">Link</th><th className="py-2 pr-4">Status</th><th />
                        </tr></thead>
                        <tbody>
                            {contacts.map((c) => (
                                <tr key={c.id} className="border-b border-stone-100" data-testid={`fb-contact-${c.id}`}>
                                    <td className="py-2.5 pr-4 font-bold text-navy">{c.name}</td>
                                    <td className="py-2.5 pr-4">{c.phone}</td>
                                    <td className="py-2.5 pr-4 text-stone-500">{c.visit_date || "—"}</td>
                                    <td className="py-2.5 pr-4 font-mono text-xs text-stone-400">…{c.token_preview}</td>
                                    <td className="py-2.5 pr-4">
                                        <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${c.submitted_at ? "bg-green-100 text-green-700" : c.status === "revoked" ? "bg-red-100 text-red-600" : "bg-gold/20 text-yellow-800"}`} data-testid={`fb-contact-status-${c.id}`}>
                                            {c.submitted_at ? "Submitted" : c.status === "revoked" ? "Revoked" : "Invited"}
                                        </span>
                                    </td>
                                    <td className="py-2.5 whitespace-nowrap">
                                        {c.link && (
                                            <button onClick={() => { navigator.clipboard.writeText(c.link); toast.success("Feedback link copied"); }} title="Copy feedback link" data-testid={`fb-copy-link-${c.id}`} className="text-stone-400 hover:text-navy mr-2"><Copy className="h-4 w-4" /></button>
                                        )}
                                        {!c.submitted_at && (
                                            <>
                                                <button onClick={() => regen(c)} title="Regenerate & copy link" data-testid={`fb-regen-${c.id}`} className="text-stone-400 hover:text-navy mr-2"><RefreshCw className="h-4 w-4" /></button>
                                                {c.status !== "revoked" && <button onClick={async () => { await revokeContact(token, c.id); toast.success("Link revoked"); load(); }} title="Revoke link" data-testid={`fb-revoke-${c.id}`} className="text-stone-400 hover:text-red-600"><Ban className="h-4 w-4" /></button>}
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {contacts.length === 0 && <p className="py-6 text-sm text-stone-500 text-center">No contacts yet — upload a sheet above.</p>}
                </div>
            )}
        </div>
    );
}

/* ---------------- Responses ---------------- */
function ResponsesPanel({ token, eventId }) {
    const [responses, setResponses] = useState(null);
    const [sentiment, setSentiment] = useState("");
    const [openBasis, setOpenBasis] = useState(null);

    const load = useCallback(async () => {
        try { setResponses((await fetchFeedbackResponses(token, { event_id: eventId, sentiment: sentiment || undefined })).responses); }
        catch { toast.error("Load failed"); }
    }, [token, eventId, sentiment]);
    useEffect(() => { setResponses(null); load(); }, [load]);

    const override = async (r, s) => {
        try {
            await overrideSentiment(token, r.id, { sentiment: s });
            toast.success(`Sentiment corrected to ${s} (original feedback untouched)`);
            load();
        } catch { toast.error("Override failed"); }
    };

    return (
        <div className="mt-4">
            <div className="flex gap-2 mb-3">
                {["", "positive", "neutral", "negative"].map((s) => (
                    <button key={s || "all"} onClick={() => setSentiment(s)} data-testid={`fb-filter-${s || "all"}`}
                        className={`rounded-full px-3.5 py-1.5 text-[11px] font-black uppercase tracking-wider ${sentiment === s ? "bg-navy text-white" : "bg-stone-100 text-stone-500"}`}>
                        {s || "All"}
                    </button>
                ))}
            </div>
            {responses === null ? <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-saffron" /></div> : (
                <div className="space-y-3">
                    {responses.map((r) => {
                        const eff = r.sentiment_override || r.sentiment;
                        return (
                            <div key={r.id} className="rounded-xl border border-stone-200 p-4" data-testid={`fb-response-${r.id}`}>
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="font-bold text-navy">{r.child_name}</span>
                                    <span className="text-xs text-stone-400">{new Date(r.submitted_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</span>
                                    <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${SENT_CHIP[eff]}`} data-testid={`fb-sentiment-${r.id}`}>
                                        {eff}{r.sentiment_override ? " (corrected)" : ""}
                                    </span>
                                    {r.coupon && <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${COUPON_CHIP[r.coupon.status]}`}>{r.coupon.code} · {r.coupon.status}</span>}
                                    <button onClick={() => setOpenBasis(openBasis === r.id ? null : r.id)} className="ml-auto text-stone-400 hover:text-navy" title="Why this classification" data-testid={`fb-basis-${r.id}`}><ChevronDown className="h-4 w-4" /></button>
                                </div>
                                <div className="mt-2 flex flex-wrap gap-3 text-xs text-stone-600">
                                    {Object.entries(r.ratings).map(([k, v]) => (
                                        <span key={k} className="inline-flex items-center gap-0.5">{k.replace("_", " ")}: <span className="font-bold text-navy inline-flex items-center">{v}<Star className="h-3 w-3 fill-gold text-gold" /></span></span>
                                    ))}
                                    <span>recommend: <span className="font-bold text-navy">{r.recommend ? "yes" : "no"}</span></span>
                                </div>
                                {r.comment && <p className="mt-2 text-sm text-stone-600 italic">"{r.comment}"</p>}
                                {openBasis === r.id && <p className="mt-2 text-[11px] text-stone-400 font-mono" data-testid={`fb-basis-text-${r.id}`}>{r.sentiment_basis}{r.override_note ? ` · override note: ${r.override_note} (${r.override_by})` : ""}</p>}
                                <div className="mt-2 flex gap-1.5">
                                    {["positive", "neutral", "negative"].filter((s) => s !== eff).map((s) => (
                                        <button key={s} onClick={() => override(r, s)} data-testid={`fb-override-${r.id}-${s}`}
                                            className="rounded-full border border-stone-200 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-stone-500 hover:border-navy hover:text-navy">
                                            Mark {s}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                    {responses.length === 0 && <p className="py-6 text-sm text-stone-500 text-center">No feedback yet for this filter.</p>}
                </div>
            )}
        </div>
    );
}

/* ---------------- Coupons ---------------- */
function CouponsPanel({ token, eventId }) {
    const [coupons, setCoupons] = useState(null);
    const load = useCallback(async () => {
        try { setCoupons((await fetchCoupons(token, { event_id: eventId })).coupons); } catch { toast.error("Load failed"); }
    }, [token, eventId]);
    useEffect(() => { setCoupons(null); load(); }, [load]);

    return (
        <div className="mt-4 overflow-x-auto">
            {coupons === null ? <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-saffron" /></div> : (
                <table className="w-full text-sm">
                    <thead><tr className="text-left text-[11px] uppercase tracking-wider text-stone-500 border-b border-stone-200">
                        <th className="py-2 pr-4">Code</th><th className="py-2 pr-4">Family</th><th className="py-2 pr-4">Issued</th><th className="py-2 pr-4">Expires</th><th className="py-2 pr-4">Status</th><th />
                    </tr></thead>
                    <tbody>
                        {coupons.map((c) => (
                            <tr key={c.id} className="border-b border-stone-100" data-testid={`fb-coupon-${c.code}`}>
                                <td className="py-2.5 pr-4 font-mono font-bold text-navy">{c.code}</td>
                                <td className="py-2.5 pr-4">{c.name || "—"}</td>
                                <td className="py-2.5 pr-4 text-stone-500">{new Date(c.issued_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</td>
                                <td className="py-2.5 pr-4 text-stone-500">{c.expires_at}</td>
                                <td className="py-2.5 pr-4"><span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${COUPON_CHIP[c.status] || COUPON_CHIP.locked}`} data-testid={`fb-coupon-status-${c.code}`}>{c.status}</span></td>
                                <td className="py-2.5">
                                    {c.status === "active" && (
                                        <button onClick={async () => { if (window.confirm(`Revoke ${c.code}?`)) { await revokeCoupon(token, c.id); toast.success("Coupon revoked"); load(); } }}
                                            data-testid={`fb-coupon-revoke-${c.code}`} className="text-stone-400 hover:text-red-600"><Ban className="h-4 w-4" /></button>
                                    )}
                                    <button onClick={() => { navigator.clipboard.writeText(c.code); toast.success("Copied"); }} className="text-stone-400 hover:text-navy ml-2" title="Copy code"><Copy className="h-4 w-4" /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
            {coupons?.length === 0 && <p className="py-6 text-sm text-stone-500 text-center">No coupons yet — they're auto-issued when a parent submits feedback.</p>}
        </div>
    );
}
